<template>
  <div class="chat-page">
    <div v-animate-css="'slideInDown'">
      <v-icon @click="back" class="closeBtn">keyboard_backspace</v-icon>
      <div @click="openProfile" class="chat__avatar-floating">
        <ProfilePic
          v-if="messages"
          :isContactIdOnline="isContactIdOnline"
          :pic="passedLogo ? passedLogo : messages.profile_pic"
        />
      </div>
      <div @click="openProfile" class="chat__header">
        <span class="text-xs-left">
          <b>{{ contactName }}</b>
        </span>
      </div>
    </div>

    <div class="chat">
      <div class="full-spinner" v-if="!messages">
        <v-progress-circular indeterminate color="green"></v-progress-circular>
      </div>

      <div v-show="messages" class="chat__wrapper">
        <div v-for="(message, i) in chat" :key="i" class="chat__wrapper-inner">
          <div
            class="chat__message"
            :class="[
              message.sender_id === $store.state.user_id ? 'chat__message-own' : '',
              message.image ? 'thin-padding' : '',
            ]"
          >
            <img
              @click="openImage(message.image)"
              v-if="message.image"
              :src="message.image"
              style="width: 150px"
            />
            <span
              v-else
              class="d-block chat__message-text"
              v-html="message.message.replaceAll(/\n/g, '<br/>')"
            ></span>

            <span
              v-if="message.sender_id === $store.state.user_id"
              class="d-block"
              :class="{ 'text-xs-right': message.sender_id === $store.state.user_id }"
            >
              <div v-if="i > contactLastReadedMessage">
                <v-icon small>mail_outline</v-icon>
                <v-icon small>done</v-icon>
              </div>
              <div v-else>
                <v-icon small>visibility</v-icon>
                <v-icon small>done</v-icon>
              </div>
            </span>
          </div>
          <div class="d-block chat__message-date" v-if="message.newDay">
            <span>{{ message.newDay }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="chat__input">
      <v-textarea
        v-animate-css="'fadeInUp'"
        color="green"
        solo
        placeholder="Schreib etwas..."
        :append-icon="inputIcon"
        prepend-inner-icon="camera_enhance"
        v-model="input"
        rows="1"
        no-resize
        @click:append="send"
        @click:prepend-inner="triggerUpload"
        type="text"
      />
    </div>

    <MatchSheetPlayer
      v-if="selectedProfile && contactType === 'player'"
      :selectedProfile="selectedProfile"
      @close="showProfile.player = false"
      :show="showProfile.player"
      :showMatchButtons="false"
    ></MatchSheetPlayer>

    <MatchSheetTrainer
      v-if="selectedProfile && contactType === 'trainer'"
      :selectedProfile="selectedProfile"
      @close="showProfile.trainer = false"
      :show="showProfile.trainer"
      :showMatchButtons="false"
    ></MatchSheetTrainer>

    <MatchSheetTeam
      v-if="selectedProfile && contactType === 'teams'"
      :selectedProfile="selectedProfile"
      @close="showProfile.teams = false"
      :show="showProfile.teams"
      :showMatchButtons="false"
    ></MatchSheetTeam>

    <MatchSheetScout
      v-if="selectedProfile && contactType === 'scout'"
      :selectedProfile="selectedProfile"
      @close="showProfile.scout = false"
      :show="showProfile.scout"
      :showMatchButtons="false"
    ></MatchSheetScout>

    <!-- uplaod picture and send -->
    <input
      id="imageUpload"
      ref="fileInput"
      type="file"
      name="profile_photo"
      accept="image/*"
      @change="croppie"
    />
    <CroppieDialog
      ref="croppieDialog"
      :circle="false"
      :image="imageSrc"
      :show.sync="showCroppie"
      @upload="uploadAndSend"
    />

    <v-dialog
      v-model="showImageDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <div class="full-image-dialog">
        <img :src="imageToShow" width="300" />
        <v-btn class="menu-button main-small" @click="showImageDialog = false"
          >Schließen</v-btn
        >
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import Axios from 'axios';
import { db } from '@/db';
import ProfilePic from '@/components/chat/ProfilePic';
import MatchSheetPlayer from '@/components/match/PlayerSheet';
import MatchSheetTeam from '@/components/match/TeamSheet';
import MatchSheetTrainer from '@/components/match/TrainerSheet';
import MatchSheetScout from '@/components/match/ScoutSheet';

import croppieMixin from '../../mixins/croppie';
import CroppieDialog from '../../components/dialogs/CroppieDialog';
import { cloudFunctions } from '../../config';

export default {
  name: 'Chat',

  components: {
    ProfilePic,
    MatchSheetPlayer,
    MatchSheetTeam,
    MatchSheetTrainer,
    MatchSheetScout,
    CroppieDialog,
  },

  mixins: [croppieMixin],

  data: () => ({
    input: '',
    chat_id: '',
    messages: null,
    passedLogo: null,
    contactNameInput: '',
    sending: false,
    selectedProfile: null,
    showProfile: {
      trainer: false,
      player: false,
      teams: false,
      scout: false,
    },
    contactByDetails: null,
    showImageDialog: false,
    imageToShow: null,
    messagesStatusUpdatedStopWatcher: false,
    lastDate: '',
  }),

  watch: {
    messages(newValue) {
      // update only once to avoid endless loop after messages collection update
      if (!this.messagesStatusUpdatedStopWatcher) {
        // update readed messages till last message id present in chat
        const currentMessagesStatus = newValue.messagesStatus
          ? newValue.messagesStatus
          : [];
        const myLastReadedMessage = {
          last_readed_message_index:
            newValue.chat && newValue.chat.length ? newValue.chat.length - 1 : 0,
          user_id: this.user_data.id,
        };
        let updatedMessagesStatus = [];

        if (currentMessagesStatus.length) {
          updatedMessagesStatus = currentMessagesStatus.filter(
            (m) => m.user_id !== this.user_data.id,
          );
        }
        updatedMessagesStatus.push(myLastReadedMessage);

        db.collection('messages').doc(this.chat_id).update({
          messagesStatus: updatedMessagesStatus,
        });
        this.messagesStatusUpdatedStopWatcher = true;
      }
    },

    chat(newVal, oldVal) {
      if (this.arraysEqual(newVal, oldVal)) {
        return;
      }

      // update last message read in matches
      console.log('update last readed in open chat after receiving message');
      const userChat = this.userMatches.find((m) => m.chat_id === this.chat_id);

      if (
        userChat &&
        userChat.lastMessageDetails &&
        userChat.lastMessageDetails.creatorId !== this.user_data.id
      ) {
        const readedLastMessage = {
          ...userChat.lastMessageDetails,
          recieverRead: true,
        };

        userChat.lastMessageDetails = readedLastMessage;
        // this.$store.state.newMessagesCount -= 1;

        db.collection('matches').doc(this.chat_id).update({
          lastMessageDetails: readedLastMessage,
        });
      }

      // he wrote message, I have chat open
      // update messages sent by him, that I read them
      if (newVal.sender_id !== this.user_data.id) {
        const currentMessagesStatus = this.messages.messagesStatus
          ? this.messages.messagesStatus
          : [];
        const myLastReadedMessage = {
          last_readed_message_index: newVal && newVal.length ? newVal.length - 1 : 0,
          user_id: this.user_data.id,
        };
        let updatedMessagesStatus = [];

        if (currentMessagesStatus.length) {
          updatedMessagesStatus = currentMessagesStatus.filter(
            (m) => m.user_id !== this.user_data.id,
          );
        }
        updatedMessagesStatus.push(myLastReadedMessage);

        db.collection('messages').doc(this.chat_id).update({
          messagesStatus: updatedMessagesStatus,
        });
      }
    },
  },

  computed: {
    ...mapState([
      'user_data',
      'userType',
      'userMatches',
      'chatImageToSave',
      'onlineChatUsers',
    ]),

    chat() {
      return this.messages
        ? this.messages.chat.map((m) => {
            // if message from new day, add variable to show it in chat view
            if (m.timestamp) {
              const newDay = moment(m.timestamp).format('DD.MM.YYYY');
              if (this.lastDate !== newDay) {
                this.lastDate = newDay;
                return {
                  ...m,
                  newDay,
                };
              }
              this.lastDate = newDay;
              return m;
            }
            return m;
          })
        : [];
    },

    contactName() {
      if (this.messages) {
        return this.contactNameInput
          ? this.contactNameInput
          : this.messages.player_name;
      }
      return '';
    },

    contactType() {
      return this.contactByDetails ? this.contactByDetails.type : this.messages.type;
    },

    contactId() {
      return this.chat_id.replace(this.user_data.id, '');
    },

    contactLastReadedMessage() {
      if (!this.messages || !this.messages.messagesStatus) {
        return -1;
      }

      const lastReaded = this.messages.messagesStatus.find(
        (m) => m.user_id === this.contactId,
      );
      if (!lastReaded) {
        return -1;
      }

      return lastReaded.last_readed_message_index;
    },

    isContactIdOnline() {
      return this.onlineChatUsers.includes(this.contactId);
    },

    inputIcon() {
      if (this.input.length > 0 && !this.sending) {
        return 'send';
      }

      if (this.sending) {
        return 'cached';
      }

      return '';
    },
  },

  methods: {
    arraysEqual(a1, a2) {
      return (
        a1.length === a2.length && a1.every((o, idx) => this.objectsEqual(o, a2[idx]))
      );
    },

    objectsEqual(o1, o2) {
      return typeof o1 === 'object' && Object.keys(o1).length > 0
        ? Object.keys(o1).length === Object.keys(o2).length &&
            Object.keys(o1).every((p) => this.objectsEqual(o1[p], o2[p]))
        : o1 === o2;
    },
    back() {
      this.$router.go(-1);
    },

    triggerUpload(img) {
      this.chatImgToUpload = img;
      this.$refs.fileInput.click();
    },

    async uploadAndSend() {
      await this.cropChatPhoto();
      if (this.chatImageToSave) {
        await this.send(true, true);
      }
    },

    async send(e, image = false) {
      if ((!this.input.length || this.sending) && !image) {
        return;
      }
      this.sending = true;

      const new_message = {
        sender_id: this.$store.state.user_id,
        timestamp: Date.now(),
      };

      if (image) {
        new_message.image = this.chatImageToSave;
      } else {
        new_message.message = this.input;
      }

      await db
        .collection('messages')
        .doc(this.chat_id)
        .update({
          timestamp: Date.now(),
          chat: [...this.chat, new_message],
        });

      const lastMessageDetails = {
        creatorId: this.user_data.id,
        recieverRead: false,
      };

      await db.collection('matches').doc(this.chat_id).update({
        timestamp: Date.now(),
        lastMessage: this.input,
        lastMessageDetails,
      });

      // update reciever account with random number to trigger chatUpdate there, showing new message bubble
      const recieverId = this.chat_id.replace(this.user_data.id, '');
      const recieverCollection = this.getContactCollection();

      await db.collection(recieverCollection).doc(recieverId).update({
        messageRecieved: Math.random(),
      });

      const recieverData = await db
        .collection(recieverCollection)
        .doc(recieverId)
        .get()
        .then((snapshot) => snapshot.data());

      // send email if allowed or if user didn't login after feature deployed and it's should be default true
      if (
        recieverData.messagingEmailChat ||
        recieverData.messagingEmailChat === undefined
      ) {
        Axios({
          method: 'post',
          url: `${cloudFunctions}/sendNotificationEmail`,
          headers: {
            'content-type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
          data: {
            email: recieverData.email,
            name: recieverData.name || recieverData.team_name,
            type: 'message',
            senderType: this.user_data.userType,
          },
        });
      }

      if (
        recieverData.messagingToken &&
        recieverData.messagingPush &&
        recieverData.messagingPushChat
      ) {
        console.log(
          'PUSH in chat: messaging token exists: ',
          recieverData.messagingToken,
        );
        Axios({
          method: 'post',
          url: `${cloudFunctions}/sendPushMessage`,
          headers: {
            'content-type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
          data: {
            data: {
              title: 'Neue Nachricht',
              body: '📨 Du hast eine neue Nachricht.',
              longTitle: 'Neue Nachricht',
              longText:
                '📨 Du hast eine neue Nachricht. Logge dich jetzt ein und checke deine Chats. 📲',
              image:
                'https://www.chance4talents.app/img/icons/android-chrome-192x192.png',
            },
            token: recieverData.messagingToken,
          },
        });
      }

      // if (
      //   recieverData.safariToken &&
      //   recieverData.messagingPush &&
      //   recieverData.messagingPushChat
      // ) {
      //   console.log('safari token exists: ', recieverData.safariToken);
      //   Axios({
      //     method: 'post',
      //     url: `${cloudFunctions}/sendNotificationSafari`,
      //     headers: {
      //       'content-type': 'application/json',
      //       'Access-Control-Allow-Origin': '*',
      //     },
      //     data: {
      //       type: 'message',
      //       deviceId: recieverData.safariToken,
      //     },
      //   });
      // }
      this.input = '';
      this.sending = false;
      this.$store.state.chatImageToSave = null;
    },

    getContactCollection() {
      let collection;

      if (this.contactType === 'player') {
        collection = 'users';
      }
      if (this.contactType === 'teams') {
        collection = 'teams';
      }
      if (this.contactType === 'trainer') {
        collection = 'trainers';
      }
      if (this.contactType === 'scout') {
        collection = 'scouts';
      }

      return collection;
    },

    async openProfile() {
      this.$store.state.loading = true;
      const collection = this.getContactCollection();

      const profile = await db
        .collection(collection)
        .doc(this.contactId)
        .get({ source: 'server' })
        .then((snapshot) => snapshot.data());

      this.selectedProfile = profile;
      this.showProfile[this.contactType] = true;
      this.$store.state.loading = false;
    },

    openImage(url) {
      this.imageToShow = url;
      this.showImageDialog = true;
    },
  },

  // use function returning object to get access to this
  firestore() {
    return {
      messages: db.collection('messages').doc(this.$route.params.id),
    };
  },

  beforeMount() {
    this.chat_id = this.$route.params.id;
    this.passedLogo = this.$route.params.avatar;
    this.contactNameInput = this.$route.params.contactName;
    this.contactByDetails = this.$route.params.contactedBy;

    // update newMatchId if chat opended
    if (this.user_data.newMatchId === this.chat_id) {
      db.collection(this.userType).doc(this.user_data.id).update({
        newMatchId: null,
      });
    }

    // update last message read in matches
    const userChat = this.userMatches.find((m) => m.chat_id === this.chat_id);

    if (
      userChat &&
      userChat.lastMessageDetails &&
      userChat.lastMessageDetails.creatorId !== this.user_data.id
    ) {
      const readedLastMessage = {
        ...userChat.lastMessageDetails,
        recieverRead: true,
      };

      userChat.lastMessageDetails = readedLastMessage;
      this.$store.state.newMessagesCount -= 1;

      db.collection('matches').doc(this.chat_id).update({
        lastMessageDetails: readedLastMessage,
      });
    }

    // update disabled users in this chat, user will be activ in chat list
    if (
      userChat &&
      userChat.disabledBy &&
      userChat.disabledBy.includes(this.user_data.id)
    ) {
      const disabledByUpdated = userChat.disabledBy.filter(
        (u) => u !== this.user_data.id,
      );
      this.$set(userChat, 'disabledBy', disabledByUpdated);

      db.collection('matches').doc(this.chat_id).update({
        disabledBy: disabledByUpdated,
      });
    }
  },
};
</script>
